import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/aboutData';
import { contactData } from '@/mocks/homeData';

export default function AboutPage() {
  const { brand, timeline, credentials, team } = aboutData;

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9phko6b4qjhggaepul0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }

      if (response.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = (parsed as Record<string, string>)?.meta?.message
          || (parsed as Record<string, string>)?.message
          || (parsed as Record<string, string>)?.meta?.detail
          || responseText
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const jsonLdAbout = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于 17.c 起草官网",
    "description": brand.intro,
    "url": "https://17.c/about",
    "isPartOf": { "@type": "WebSite", "name": "17.c 起草官网", "url": "https://17.c" }
  });

  const jsonLdBreadcrumb = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://17.c" },
      { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://17.c/about" }
    ]
  });

  return (
    <>
      <title>关于我们 - 17.c 起草官网</title>
      <meta name="description" content="17.c 起草官网 - 专业文件起草与官网建设一站式服务平台。了解我们的品牌故事、发展历程、资质信誉及联系方式。十年行业经验，5000+企业客户的信赖之选。" />
      <link rel="canonical" href="https://17.c/about" />
      <script type="application/ld+json">{jsonLdAbout}</script>
      <script type="application/ld+json">{jsonLdBreadcrumb}</script>

      <div className="min-h-screen bg-background-50">
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            <section className="py-10 md:py-14">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading mb-4">
                    {brand.title}
                  </h1>
                  <p className="text-lg text-primary-600 font-heading mb-6">{brand.subtitle}</p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{brand.intro}</p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    <strong className="text-foreground-900">我们的使命：</strong>{brand.mission}
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    <strong className="text-foreground-900">我们的愿景：</strong>{brand.vision}
                  </p>
                </div>
                <div className="flex-1 w-full">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden">
                    <img
                      src={brand.imageUrl}
                      alt="17.c 起草官网团队"
                      title="17.c 起草官网专业团队"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 font-heading text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {brand.values.map((v) => (
                  <div
                    key={v.title}
                    className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70"
                  >
                    <h3 className="text-base font-bold text-foreground-900 mb-2 font-heading">{v.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        <section id="timeline" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-12 font-heading text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />
              <div className="space-y-10">
                {timeline.map((item, i) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 md:gap-0 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                      <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 ml-10 md:ml-0">
                        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-2">
                          {item.year}
                        </span>
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-[13px] md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1/2 mt-5" />
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="credentials" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-12 font-heading text-center">
              资质信誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {credentials.map((cred) => (
                <div
                  key={cred.title}
                  className="bg-background-100 rounded-xl p-6 md:p-8 text-center border border-background-200/70"
                >
                  <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${cred.icon} text-2xl text-accent-600`} />
                  </div>
                  <h3 className="text-base font-bold text-foreground-900 mb-2 font-heading">{cred.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cred.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-12 font-heading text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {team.map((member) => (
                <div key={member.name} className="text-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
                    <img
                      src={member.avatarUrl}
                      alt={member.name}
                      title={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-base font-bold text-foreground-900 font-heading">{member.name}</h3>
                  <p className="text-sm text-primary-600 mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-12 font-heading text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                    <p className="text-sm text-foreground-600">{contactData.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                    <p className="text-sm text-foreground-600">{contactData.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                    <p className="text-sm text-foreground-600">{contactData.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-600">{contactData.workHours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信</h4>
                    <p className="text-sm text-foreground-600">{contactData.wechat}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-bold text-foreground-900 mb-6 font-heading">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电话
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        咨询内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请描述您的需求，我们将尽快回复"
                      />
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="site-honeypot"
                    />
                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      提交咨询
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">提交成功！我们将尽快与您联系。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-accent-600 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}